import { useEffect, useState } from 'react';
import { ConfirmDialog } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { onPaywall, onPaywallReset } from '../../lib/paywallSignal';
import { useAccess } from '../../hooks/useAccess';
import { formatJalaliDate, remainingLabel, statusMeta } from '../../screens/Subscription/constants';

const DAY_MS = 86_400_000;

/** Whole days until an ISO instant; null when there is no date to count to. */
function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.ceil((ms - Date.now()) / DAY_MS);
}

/**
 * The one place a `SUBSCRIPTION_REQUIRED` failure is explained.
 *
 * Mounted once in the shell, because the failure can come from anywhere: the
 * receptionist booking an appointment, the manager adding a doctor, the admin
 * defining a role. Before this, no client code branched on the code at all — the
 * BFF's whole reason for emitting `SUBSCRIPTION_REQUIRED` separately from
 * `FORBIDDEN` (so the client can offer a renew button instead of telling a clinic
 * owner they lack access to their own clinic) had no consumer, and every blocked
 * write surfaced as a generic red toast with no way forward.
 *
 * The two codes stay apart here too: `FORBIDDEN` is still a plain toast at the
 * call site. A person who genuinely lacks a permission has nothing to click.
 *
 * The call to action depends on what the reader can actually do. Someone with
 * `clinic.billing.manage` is offered «تمدید اشتراک»; everyone else is offered
 * «مشاهدهٔ وضعیت اشتراک», because sending a receptionist to a page whose buttons
 * they cannot press would be a second dead end. Both land on the same view —
 * one to act, one to read and go tell someone.
 *
 * `renewUrl` arrives in the payload and is deliberately ignored: the client
 * navigates by its own route map, never by a string the server chose.
 */
export default function PaywallDialog({ onRenew }) {
  const { t, formatDate } = useTranslation();
  const { can } = useAccess();
  const [info, setInfo] = useState(null);

  useEffect(() => onPaywall((payload) => {
    // Last one wins. A screen firing two blocked mutations at once would
    // otherwise queue two identical dialogs for the same fact.
    setInfo(payload);
  }), []);

  // Closed from outside on a clinic switch: this dialog's state lives in a module
  // singleton, so without it a renewal wall raised at a lapsed clinic followed the
  // person into an entitled one (design E11).
  //
  // Both subscriptions sit ABOVE the `if (!info)` return, and must: a hook below an
  // early return runs on some renders and not others, and React counts hooks per
  // render — which is exactly the «Rendered more hooks than during the previous
  // render» crash that took the whole dialog down the first time this was added.
  useEffect(() => onPaywallReset(() => setInfo(null)), []);

  if (!info) return null;

  const meta = statusMeta(info.status, t);
  const canManageBilling = can('clinic.billing.manage');
  const endText = formatJalaliDate(info.currentPeriodEnd, formatDate);
  // The extensions carry the period end, not a countdown — the BFF computes
  // `daysRemaining` only for the capabilities query.
  const remaining = remainingLabel(daysUntil(info.currentPeriodEnd), t);

  const close = () => setInfo(null);

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => { if (!open) close(); }}
      title={t('clinic.paywall.title')}
      // The server's own Persian sentence names the operation that was blocked
      // and what unblocks it; `meta.description` is the generic fallback for a
      // status whose message did not travel.
      description={info.message || meta.description}
      confirmLabel={canManageBilling ? t('clinic.actions.goToBilling') : t('clinic.paywall.view')}
      cancelLabel={t('clinic.paywall.close')}
      onConfirm={() => { close(); onRenew?.(); }}
    >
      {/* The test hook lives here, not on ConfirmDialog: that component takes a
          fixed prop list and forwards no `...rest`, so a `data-testid` on it is
          silently dropped. */}
      <div data-testid="paywall-dialog" data-status={info.status || ''}>
        {endText ? (
          <p className="m-0 text-xs text-muted-foreground" data-testid="paywall-dialog-period">
            {t('subscription.fields.validUntil')} {endText}
            {remaining ? ` · ${remaining}` : ''}
          </p>
        ) : null}
      </div>
    </ConfirmDialog>
  );
}
